import React, { useState } from 'react';
import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';

/**
 * Compact collapsible progress pill rendered in the main content area.
 * Used by both Ask mode (agentic workflow steps) and Swarm mode (session steps).
 *
 * Collapsed: a small pill showing only the spinner + label + chevron.
 * Expanded: a dropdown list of all steps anchored below the pill.
 */
const InlineProgressCard = ({ steps = [], title = 'Processing' }) => {
  const [expanded, setExpanded] = useState(false);

  if (!steps || steps.length === 0) return null;

  // Collapse consecutive identical statuses (e.g. repeated keepalive
  // "Research in progress..." lines, or a status echoed by both a failed
  // attempt and a fallback re-dispatch) so the flow reads cleanly instead
  // of repeating the same line. Only adjacent duplicates are removed —
  // distinct steps and legitimate non-adjacent repeats are preserved.
  const displaySteps = steps.reduce((acc, step) => {
    const label = step.message || step.status;
    const prev = acc[acc.length - 1];
    const prevLabel = prev ? (prev.message || prev.status) : null;
    if (label !== prevLabel) acc.push(step);
    return acc;
  }, []);

  return (
    <div style={{ display: 'inline-block', position: 'relative', margin: '6px 0' }}>
      {/* Pill trigger */}
      <div
        onClick={() => setExpanded(e => !e)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(v => !v); }}
        aria-expanded={expanded}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px 4px 8px',
          borderRadius: 20,
          border: '1px solid var(--border-color, #ddd)',
          background: 'var(--panel-background, var(--input-background))',
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Live spinner dot */}
        <div style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          backgroundColor: 'var(--primary-color, #007bff)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div
            className="loading-spinner"
            style={{ width: '5px', height: '5px', border: '1.5px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }}
          />
        </div>

        {/* Label */}
        <span style={{
          fontSize: '0.72rem', fontWeight: 700,
          color: 'var(--text-secondary, #888)',
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          {title}
        </span>

        {/* Chevron */}
        <div style={{ color: 'var(--text-secondary, #888)', display: 'flex', alignItems: 'center' }}>
          {expanded ? <RiArrowUpSLine size={14} /> : <RiArrowDownSLine size={14} />}
        </div>
      </div>

      {/* Expanded step list — floats below the pill */}
      {expanded && displaySteps.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          zIndex: 200,
          minWidth: '240px',
          maxWidth: '360px',
          maxHeight: 'min(50vh, 420px)',
          overflowY: 'auto',
          border: '1px solid var(--border-color, #ddd)',
          borderRadius: 8,
          background: 'var(--panel-background, var(--input-background))',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          padding: '6px 0 8px',
        }}>
          {displaySteps.map((step, i) => {
            const isLatest = i === displaySteps.length - 1;
            return (
              <div
                key={step.id || `${step.timestamp}-${i}`}
                style={{
                  display: 'flex', alignItems: 'center',
                  padding: '4px 12px',
                  backgroundColor: isLatest ? 'var(--hover-color, rgba(0,123,255,0.07))' : 'transparent',
                }}
              >
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: isLatest ? 'var(--primary-color, #007bff)' : '#28a745',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginRight: 8,
                }}>
                  {isLatest
                    ? <div className="loading-spinner" style={{ width: '5px', height: '5px', border: '1.5px solid rgba(255,255,255,0.3)', borderLeftColor: '#fff' }} />
                    : <span style={{ color: 'white', fontSize: '6px', lineHeight: 1 }}>✓</span>}
                </div>
                <span style={{
                  fontSize: '0.78rem',
                  color: isLatest ? 'var(--text-color)' : 'var(--text-secondary, #888)',
                  fontWeight: isLatest ? '500' : 'normal',
                  lineHeight: 1.35,
                }}>
                  {step.message || step.status}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InlineProgressCard;
